// acquisition.js — remember which caption a visitor arrived from.
//
// Each social caption carries its own short link (/s/yt, /s/ig, /s/tt), so traffic can be told
// apart and a signup can be attributed to the channel that produced it.
//
// STORED, NOT SENT IMMEDIATELY, because the click and the signup are almost never the same
// moment: somebody taps a TikTok caption, watches a video, comes back tomorrow and makes an
// account. The slug therefore has to outlive the session that saw it.
//
// FIRST TOUCH WINS. A visitor who arrives from YouTube and later returns via Instagram is a
// YouTube acquisition — the first link is the one that did the work of finding them. Writing
// the newest slug would answer "where did they most recently come from", which is a different
// and much less useful question. The backend enforces the same rule on its side by only
// stamping the field when it creates the user document.

const KEY = "magi_acq_src";
const PREFIX = "/s/";

// Mirrors ACQUISITION_SOURCES in api/user_router.py. Kept short because these are typed into
// captions by hand, and validated on BOTH sides — the backend drops anything unrecognised, so
// this list is a convenience rather than the security boundary.
export const SOURCES = {
  yt: "YouTube",
  ig: "Instagram",
  tt: "TikTok",
  x: "X",
  li: "LinkedIn",
  fb: "Facebook",
  rd: "Reddit",
  nl: "Newsletter",
};

/** The slug from /s/<slug> OR from a ?s=<slug> query. Returns null otherwise.
 *
 * TWO SHAPES BECAUSE CAPTIONS CARRY TWO KINDS OF LINK. The site itself gets the short path
 * form; a link to a specific video already has a path, so it is tagged with ?s= instead.
 * Reading only the path silently dropped every deep link's attribution — the visitor arrived
 * on a real video page and counted as organic.
 */
export function sourceFromLocation(pathname, search) {
  if (pathname && pathname.startsWith(PREFIX)) {
    const slug = pathname.slice(PREFIX.length).split("/")[0].trim().toLowerCase();
    if (slug in SOURCES) return slug;
  }
  try {
    const q = new URLSearchParams(search || "").get("s");
    const slug = (q || "").trim().toLowerCase();
    if (slug in SOURCES) return slug;
  } catch { /* malformed query — nothing to attribute */ }
  return null;
}

/** Record a slug if we have not already recorded one. Returns the slug now held. */
export function rememberSource(slug) {
  if (!slug || !(slug in SOURCES)) return getSource();
  try {
    if (!localStorage.getItem(KEY)) localStorage.setItem(KEY, slug);
  } catch { /* private mode / storage disabled — attribution is best-effort, never load-bearing */ }
  return getSource();
}

/** The remembered slug, or null. */
export function getSource() {
  try {
    const v = localStorage.getItem(KEY);
    return v && v in SOURCES ? v : null;
  } catch {
    return null;
  }
}
